<template>
  <header class="landing-header fixed top-0 left-0 right-0 z-[100]">
    <div class="mx-auto flex w-full max-w-none items-center justify-between px-4 sm:px-8 lg:px-12 xl:px-[120px] py-4">
      <!-- Logo & Brand -->
      <div class="flex items-center gap-3 lg:gap-[56px]">
        <a :href="brandUrl" class="flex items-center">
          <img :src="logoSrc" :alt="brandName" class="h-10 w-auto object-contain" />
        </a>

        <!-- Tablet Menu Button (cách logo 12px) -->
        <button ref="menuButtonRef" type="button" class="hidden md:inline-flex lg:hidden h-6 w-6 items-center justify-center" @click="toggleMenu">
          <span class="sr-only">Mở menu</span>
          <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#000000">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        <!-- Navigation Menu (PC) -->
        <nav class="hidden items-center gap-[32px] lg:flex">
          <router-link
            v-for="item in navLinks"
            :key="item.to || item.label"
            :to="item.to"
            :class="['flex items-center gap-1 text-sm font-semibold text-[#36393e] transition hover:text-hilab cursor-pointer', { 'text-hilab': isActive(item) }]"
            @click="handleNavClick($event, item)"
          >
            <span>{{ item.label }}</span>
            <svg v-if="item.hasDropdown" class="h-5 w-5 text-[#36393e]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </router-link>
        </nav>
      </div>

      <!-- Action Buttons (Tablet & PC) -->
      <div class="hidden items-center gap-3 md:flex">
        <a
          href="/app/login"
          class="inline-flex items-center justify-center gap-2 h-10 min-w-[100px] px-4 py-2 rounded-full border border-primary-500 text-sm font-semibold text-[#8326d9] bg-transparent transition-all duration-200 hover:bg-[#8326d9]/5 hover:border-primary-500"
        >
          Đăng nhập
        </a>
        <a
          :href="primaryCtaUrl"
          class="inline-flex items-center justify-center gap-2 h-10 min-w-[100px] px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-hilab to-[#4A1878] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] transition-all duration-200 hover:from-[#7a26c5] hover:to-[#3d1563] hover:shadow-[0px_6px_8px_0px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
        >
          Dùng thử MIỄN PHÍ
        </a>
      </div>

      <!-- Mobile Menu Button (góc phải) -->
      <button ref="menuButtonRef" type="button" class="inline-flex h-6 w-6 items-center justify-center md:hidden" @click="toggleMenu">
        <span class="sr-only">Mở menu</span>
        <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="#000000">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </div>

    <!-- Menu Popup (Teleport to body for z-index) -->
    <Teleport to="body">
      <Transition name="fade-slide">
        <div v-if="isOpen" class="fixed z-[9999]" :style="menuStyle">
          <div class="w-[200px] bg-white rounded-xl p-4 shadow-[0px_0px_16px_0px_rgba(0,0,0,0.12)] flex flex-col gap-4">
            <!-- Navigation Links -->
            <router-link
              v-for="item in navLinks"
              :key="item.to || item.label"
              :to="item.to"
              :class="['flex items-center gap-1 text-sm font-semibold leading-[1.5] text-[#36393E] transition hover:text-hilab cursor-pointer', { 'text-hilab': isActive(item) }]"
              @click="
                handleNavClick($event, item);
                closeMenu();
              "
            >
              <span>{{ item.label }}</span>
              <svg v-if="item.hasDropdown" class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </router-link>

            <!-- Action Buttons (Mobile only) -->
            <div v-if="isMobile" class="flex flex-col gap-3 mt-2">
              <a
                href="/app/login"
                class="inline-flex items-center justify-center gap-2 h-10 min-w-[100px] px-4 py-2 rounded-full border border-primary-500 text-sm font-semibold text-[#8326d9] bg-transparent transition-all duration-200 hover:bg-[#8326d9]/5 hover:border-primary-500"
                @click="closeMenu"
              >
                Đăng nhập
              </a>
              <a
                :href="primaryCtaUrl"
                class="inline-flex items-center justify-center gap-2 h-10 min-w-[100px] px-4 py-2 rounded-full text-sm font-semibold text-white bg-gradient-to-r from-hilab to-[#4A1878] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] transition-all duration-200 hover:from-[#7a26c5] hover:to-[#3d1563] hover:shadow-[0px_6px_8px_0px_rgba(0,0,0,0.08)] hover:-translate-y-0.5 active:translate-y-0 active:shadow-[0px_2px_4px_0px_rgba(0,0,0,0.04)]"
                @click="closeMenu"
              >
                Dùng thử MIỄN PHÍ
              </a>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router';

const defaultLogo = '/brand-assets/logo_v2.svg';

const props = defineProps({
  brandName: {
    type: String,
    required: true,
  },
  brandUrl: {
    type: String,
    required: true,
  },
  logoUrl: {
    type: String,
    default: '',
  },
  primaryCtaUrl: {
    type: String,
    required: true,
  },
  navLinks: {
    type: Array,
    default: () => [],
  },
});

const route = useRoute();
const isOpen = ref(false);
const menuButtonRef = ref(null);
const menuPosition = ref({ top: 0, left: 0 });
const isMobile = ref(false);

// Check if mobile
const checkMobile = () => {
  isMobile.value = window.innerWidth < 768;
};

onMounted(() => {
  checkMobile();
  window.addEventListener('resize', checkMobile);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile);
});

const brandName = computed(() => props.brandName || '');
const brandUrl = computed(() => props.brandUrl || '/');
const logoSrc = computed(() => defaultLogo);
const primaryCtaUrl = computed(() => props.primaryCtaUrl || '/auth/signup');
const navLinks = computed(() => (Array.isArray(props.navLinks) ? props.navLinks : []));

// Menu style based on button position
const menuStyle = computed(() => ({
  top: `${menuPosition.value.top}px`,
  left: `${menuPosition.value.left}px`,
}));

const toggleMenu = event => {
  if (!isOpen.value) {
    // Get button position from event target
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();

    // Position menu below button, aligned right for mobile, left for tablet
    if (isMobile.value) {
      menuPosition.value = {
        top: rect.bottom + 8,
        left: rect.right - 200, // Align right edge with button
      };
    } else {
      menuPosition.value = {
        top: rect.bottom + 8,
        left: rect.left, // Align left edge with button
      };
    }
  }
  isOpen.value = !isOpen.value;
};

const closeMenu = () => {
  isOpen.value = false;
};

// Handle navigation click
const handleNavClick = (event, item) => {
  if (item.hash) {
    event.preventDefault();
    const [path, hash] = item.to.split('#');

    if (path === route.path) {
      const targetElement = document.getElementById(hash);
      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    } else {
      setTimeout(() => {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          targetElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 100);
    }
  }
};

// Check if nav item is active
const isActive = item => {
  if (item.hash) {
    const [path, hash] = item.to.split('#');
    return route.path === path && route.hash === `#${hash}`;
  }

  if (item.to === '/docs' || item.to === '/features') {
    return route.path === item.to || route.path.startsWith(item.to + '/');
  }

  if (item.to === '/') {
    return route.path === '/' && (!route.hash || route.hash === '');
  }

  return route.path === item.to;
};
</script>

<style scoped>
.landing-header {
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.02);
}

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
